import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import { AuthEnums, CustomerEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { CustomerFormTypes } from "models/formTypes";
import { ServiceConstants } from "utils/constants";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import useAPIClientHelper from "./useAPIClientHelper";

function useCustomerService() {
  const { apiClientInstance } = useAPIClientHelper();

  const getCustomers = async (
    pagination: WintUtilsWebApi.Filter,
    filter: CustomerFormTypes.Filter,
    skipUndefinedConversion?: boolean
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      skipUndefinedConversion: skipUndefinedConversion,
    }).CustomerClient.GetByFilter({
      ...pagination,
      ...reportToAPIFilter(filter),
    });
  };
  const reportToAPIFilter = (
    formFilter?: CustomerFormTypes.Filter
  ): Omit<ApiModels.CustomerFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      name: formFilter?.customerName,
      orgNumber: formFilter?.orgNumber,
      ratingFrom: formFilter?.rating?.lowest,
      ratingTo: formFilter?.rating?.highest,
      invoiceDateFrom: formFilter?.date?.start,
      invoiceDateTo: formFilter?.date?.end,
      numOfInvoicesFrom: formFilter?.numOfInvoices?.lowest,
      numOfInvoicesTo: formFilter?.numOfInvoices?.highest,
      numOfRecurringInvoicesFrom: formFilter?.numOfRecurringInvoices?.lowest,
      numOfRecurringInvoicesTo: formFilter?.numOfRecurringInvoices?.highest,
      invoicedAmountFrom: formFilter?.invoicedAmount?.lowest,
      invoicedAmountTo: formFilter?.invoicedAmount?.highest,
      unpaidAmountFrom: formFilter?.unpaidAmount?.lowest,
      unpaidAmountTo: formFilter?.unpaidAmount?.highest,
      inactive: formFilter?.active !== undefined ? !Boolean(formFilter.active) : undefined,
    };
  };
  const getCustomer = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.GetById(id)
      .then((res) => res.data);
  };
  const lookupGln = (organisationNumber: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.GlnLookup(organisationNumber)
      .then((res) => res.data);
  };
  const searchCustomersByOrgNr = (value: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.SearchCustomersByOrgNr(value)
      .then((res) => res.data);
  };
  const searchExternalCustomers = (searchText: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.SearchCustomer({
        searchOrgNr: "",
        searchCity: "",
        searchName: searchText,
        page: 0,
        numPerPage: 10,
        orderByProperty: "Name",
        orderByDescending: true,
        isAutoPaginating: true,
        thenByDescending: false,
      })
      .then((res) => res.data.items);
  };
  const createCustomer = (customer: ApiModels.CustomerPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).CustomerClient.Post(customer);
  };
  const updateCustomer = (id: number, customer: ApiModels.CustomerPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).CustomerClient.Put(id, customer);
  };
  const deleteReferencePerson = (referenceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReferenceClient.Delete(referenceId);
  };
  const getTodos = (customerId: number, filter?: WintUtilsWebApi.Filter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).InvoiceClient.GetTodos({
      ...(filter ?? ServiceConstants.DefaultFilter),
      customerId,
    });
  };
  const getCustomerRating = (customerId?: number, orgNr?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.GetCustomerRating({ customerId: customerId, orgNr: orgNr })
      .then((res) => res.data.rating);
  };
  const getCustomerSuggestions = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.GetTopCustomers()
      .then((res) => res.data);
  };

  const searchInternalCustomers = (searchText: string): Promise<ApiModels.CustomerSearchResponse[]> => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.GetSearchCustomersByName(searchText)
      .then((res) => res.data);
  };

  const checkCustomerExists = (customerInfo: ApiModels.ExistingCustomerCheckRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.GetCustomerWithSameDetails(customerInfo)
      .then((res) => res.data);
  };

  const getPenaltyInterestType = (customerId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.PenaltyInterestType(customerId)
      .then((res) => res.data);
  };

  const getPenaltyInterestRate = (customerId: number, penaltyInterestType: ApiModels.InvoicePenaltyInterestType) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CustomerClient.PenaltyInterestRate(customerId, penaltyInterestType)
      .then((res) => res.data);
  };

  const deleteCustomer = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).CustomerClient.Delete(id);
  };
  const importCustomerRegister = (
    importDetails: ApiModels.CustomerImport,
    options?: { hideErrorsForCodes?: string[] }
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
      ...options,
    }).CustomerClient.ImportCustomers(importDetails);
  };

  const getSettingTypes = (): SelectionItem[] => {
    return EnumHelper.getNumericItemValues(CustomerEnums.CustomerSettingType).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getTranslatedEnumValue(
          "Customer.SettingType",
          CustomerEnums.CustomerSettingType[key].toString()
        ),
      };
    });
  };

  return {
    getCustomers,
    reportToAPIFilter,
    getCustomer,
    searchCustomersByOrgNr,
    lookupGln,
    searchExternalCustomers,
    createCustomer,
    updateCustomer,
    deleteReferencePerson,
    getTodos,
    getCustomerRating,
    getCustomerSuggestions,
    searchInternalCustomers,
    checkCustomerExists,
    getPenaltyInterestType,
    getPenaltyInterestRate,
    deleteCustomer,
    importCustomerRegister,
    getSettingTypes,
  };
}

export default useCustomerService;
